import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Modal from 'react-modal';
import AuthCodeInput from 'react-auth-code-input';
import {
	validatePhoneNumber,
	validateEmail,
} from 'helpers/validation/utils/validate';
import { useCreateDispatches } from 'helpers/hooks';
import { PrimaryButton, SecondaryButton, TextFieldRow } from 'components';
import { enableTwoFactor, getTwoFactorCode } from 'services/user-services';
import MethodSwitch from './MethodSwitch';
import { getOwnUser } from 'services';
import styles from './multiFactor.module.css';
import { modalStyle } from './modalStyles';

const RESEND_COOLDOWN_SECONDS = 30;

const MultiFactorModal = () => {
	const modalOpen = useSelector((state) => state.user.authModal);
	const authCodeRef = useRef(null);
	const [method, setMethod] = useState('phone');
	const [phone, setPhone] = useState('');
	const [email, setEmail] = useState('');
	const [passcode, setPasscode] = useState('');
	const [codeSent, setCodeSent] = useState(false);
	const [resendCooldown, setResendCooldown] = useState(0);

	const { set2faModal, setSnackbar, setLoader } = useCreateDispatches();

	useEffect(() => {
		setCodeSent(false);
		setPhone('');
		setPasscode('');
	}, []);

	useEffect(() => {
		if (resendCooldown <= 0) return;
		const interval = setInterval(() => {
			setResendCooldown((prev) => prev - 1);
		}, 1000);
		return () => clearInterval(interval);
	}, [resendCooldown]);

	const onChange = ({ target }) => {
		target.value === 'email' ? setMethod('email') : setMethod('phone');
	};

	const handleCancel = () => {
		set2faModal(false);
		setCodeSent(false);
		setPhone('');
		setEmail('');
		setResendCooldown(0);
	};

	const handleError = async (error) => {
		if (error?.response?.status === 403) {
			setSnackbar({
				variant: 'error',
				message: error.response?.data?.message,
			});
		} else {
			setSnackbar({
				variant: 'error',
				message: 'There was an error processing your request',
			});
		}
		await setLoader(false);
	};

	const handleRequestCode = async () => {
		const validPhone = validatePhoneNumber(phone);
		const validEmail = validateEmail(email);
		const authValue = method === 'email' ? email : phone;
		if (validPhone || validEmail) {
			await setLoader(true);
			getTwoFactorCode(method, authValue)
				.then(async () => {
					await setLoader(false);
					setCodeSent(true);
					setResendCooldown(RESEND_COOLDOWN_SECONDS);
				})
				.catch(async (error) => {
					handleError(error);
				});
		} else {
			setLoader(false);
			setSnackbar({
				variant: 'error',
				message: `${method} is not valid`,
			});
		}
	};

	const handleValidateCode = async () => {
		const authValue = method === 'email' ? email : phone;
		if (passcode.length === 6) {
			await setLoader(true);
			enableTwoFactor(method, authValue, passcode)
				.then(async (e) => {
					await getOwnUser('fetchUpdate');
					await setLoader(false);
					setSnackbar({
						variant: 'success',
						message: 'Two-Factor Authorization is now enabled',
					});
					setCodeSent(false);
					set2faModal(false);
				})
				.catch(async (error) => {
					handleError(error);
				})
				.finally((e) => {
					authCodeRef.current.clear();
				});
		} else {
			await setLoader(false);
			setSnackbar({
				variant: 'error',
				message: 'Code is not valid',
			});
		}
	};
	const handleSubmit = () => {
		codeSent ? handleValidateCode() : handleRequestCode();
	};

	const handleResendCode = async () => {
		if (resendCooldown > 0) return;
		const authValue = method === 'email' ? email : phone;
		await setLoader(true);
		getTwoFactorCode(method, authValue)
			.then(async () => {
				await setLoader(false);
				authCodeRef.current.clear();
				setPasscode('');
				setResendCooldown(RESEND_COOLDOWN_SECONDS);
				setSnackbar({
					variant: 'success',
					message: 'A new code has been sent',
				});
			})
			.catch(async (error) => {
				handleError(error);
			});
	};

	return (
		<div style={{ zIndex: 5000 }}>
			<Modal
				ariaHideApp={false}
				style={modalStyle}
				isOpen={modalOpen}
				transparent={false}>
				<div className={styles.modalContainer}>
					<div className={styles.textContainer}>Two-Factor Authentication </div>
					<div className={styles.lowerTextContainer}>
						{!codeSent ? (
							<p>
								Enter your phone number or email to receive a code to enable
								two-factor authentication.
							</p>
						) : (
							<p>
								A message with a verification code has been sent to your
								devices. Enter the code to continue.
							</p>
						)}
					</div>
					{!codeSent ? (
						<>
							<div className={styles.textContainer}>
								<MethodSwitch value={method} onChange={onChange} />
							</div>
							{method === 'phone' ? (
								<div className={styles.inputContainer}>
									<TextFieldRow
										label={'Phone Number'}
										className={styles.reasonContainer}
										value={phone}
										onChange={(e) => setPhone(e.target.value)}
									/>
								</div>
							) : (
								<div className={styles.inputContainer}>
									<TextFieldRow
										label={'Email'}
										className={styles.reasonContainer}
										value={email}
										onChange={(e) => setEmail(e.target.value)}
									/>
								</div>
							)}
						</>
					) : (
						<AuthCodeInput
							ref={authCodeRef}
							containerClassName={styles.authContainer}
							inputClassName={styles.authInput}
							onChange={(e) => setPasscode(e)}
						/>
					)}

					<div className={styles.buttonContainer}>
						<PrimaryButton onClick={() => handleSubmit()}>Submit</PrimaryButton>
						{codeSent && (
							<SecondaryButton
								disabled={resendCooldown > 0}
								onClick={() => handleResendCode()}>
								{resendCooldown > 0
									? `Resend Code (${resendCooldown}s)`
									: 'Resend Code'}
							</SecondaryButton>
						)}
						<SecondaryButton onClick={() => handleCancel()}>
							Close
						</SecondaryButton>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default MultiFactorModal;
